import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, clubHomePath, runningPath, hikingPath } from '../utils/paths';
import { normalizeClubSport } from '../config/clubSports';
import './MyClubs.css';

const MyClubs = ({ sportFilter = null, basePath = null } = {}) => {
  const { user, isAdmin, isClubCreator } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasApprovedPlayer, setHasApprovedPlayer] = useState(false);

  const registerPath = basePath
    ? `${basePath}/clubs/register`
    : soccerPath('/clubs/register');
  const listPath = basePath
    ? `${basePath}/clubs/list`
    : soccerPath('/clubs/list');

  const checkApprovedPlayer = useCallback(async () => {
    if (!user) {
      setHasApprovedPlayer(false);
      return;
    }

    if (isAdmin() || isClubCreator()) {
      setHasApprovedPlayer(true);
      return;
    }

    try {
      const allPlayers = await api.getAllPlayers(user?.id);
      const userPlayer = allPlayers.find((p) => p.userId === user.id);
      setHasApprovedPlayer(!!userPlayer);
    } catch (err) {
      setHasApprovedPlayer(false);
    }
  }, [user, isAdmin, isClubCreator]);

  const loadClubs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getMyClubs(user.id);
      setClubs(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load clubs');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      checkApprovedPlayer();
      loadClubs();
    }
  }, [user, checkApprovedPlayer, loadClubs]);

  const visibleClubs = useMemo(() => {
    if (!sportFilter) return clubs;
    const wanted = normalizeClubSport(sportFilter);
    return clubs.filter((club) => normalizeClubSport(club.sport) === wanted);
  }, [clubs, sportFilter]);

  if (!user) {
    return (
      <div className="card">
        <h2>Please log in to view your clubs</h2>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading your clubs...</div>;
  }

  const canRegisterClub = isAdmin() || isClubCreator() || hasApprovedPlayer;
  const adminPathFor = (club) => {
    const sport = normalizeClubSport(club.sport);
    if (sport === 'RUNNING') {
      return runningPath(`/clubs/${club.id}/admin`);
    }
    if (sport === 'HIKING') {
      return hikingPath(`/clubs/${club.id}/admin`);
    }
    return soccerPath(`/clubs/${club.id}/admin`);
  };

  return (
    <div className="my-clubs">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>
          {sportFilter
            ? `My ${normalizeClubSport(sportFilter).toLowerCase()} clubs`
            : 'My Clubs'}
        </h2>
        {canRegisterClub && (
          <Link to={registerPath} className="btn btn-primary">
            Register New Club
          </Link>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {visibleClubs.length === 0 ? (
        <div className="empty-state">
          <h3>You are not a member of any{sportFilter ? ` ${normalizeClubSport(sportFilter).toLowerCase()}` : ''} clubs yet</h3>
          <p>You can register a new club or request to join an existing club.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' }}>
            {canRegisterClub && (
              <Link to={registerPath} className="btn btn-primary">
                Register Club
              </Link>
            )}
            <Link to={listPath} className="btn btn-secondary">
              Browse Clubs
            </Link>
          </div>
        </div>
      ) : (
        <div className="clubs-list">
          {visibleClubs.map((club) => (
            <div key={club.id} className="club-card">
              {club.logoUrl && (
                <img src={club.logoUrl} alt={club.name} className="club-logo-small" />
              )}
              <div className="club-info">
                <h3>{club.name}</h3>
                {club.description && <p>{club.description}</p>}
                <div className="club-status">
                  <span className={`badge ${
                    club.status === 'APPROVED' ? 'badge-success' :
                    club.status === 'PENDING_APPROVAL' ? 'badge-warning' :
                    'badge-danger'
                  }`}>
                    {club.status === 'APPROVED' ? 'Approved' :
                     club.status === 'PENDING_APPROVAL' ? 'Pending Approval' :
                     'Rejected'}
                  </span>
                </div>
              </div>
              <div className="club-actions">
                <Link to={clubHomePath(club.id, club.sport)} className="btn btn-secondary">
                  View Club
                </Link>
                {club.status === 'APPROVED' && user && club.adminUserId === user.id && (
                  <Link to={adminPathFor(club)} className="btn btn-primary">
                    Admin Dashboard
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyClubs;
